/**
 * Which game events become toasts: confirmed events only (never predictions), refusals, crewmates
 * coming and going, the connection, and a version mismatch.
 */
import { machineAt } from '@bramvasterd/terrella-rules';

import type { Game } from '@game/game';
import { machineName, refusalText, resourceName, t } from '@i18n/index';
import { haptic } from '@platform/haptics';

import { showToast } from './toastStore';

export function wireToasts(game: Game): () => void {
  let welcomedAt = -Infinity;
  let wasOnline = false;
  let warnedRules = false;
  let outageShown = false;
  const nameOf = (id: string): string => {
    const s = game.store.get();
    if (id === s.me?.id) return t('hud.you');
    return s.members.get(id)?.name ?? t('hud.crewmate');
  };
  /** The machine on a tile, lower case: "stonecutter". */
  const machineOn = (tile: number): string => {
    const confirmed = game.store.get().confirmed;
    const type = confirmed ? machineAt(confirmed, tile)?.type : undefined;
    return type ? machineName(type).toLowerCase() : t('hud.machine');
  };

  const offs = [
    game.events.on('confirmed', ({ events, mine }) => {
      for (const e of events) {
        if (e.kind === 'fell') showToast(t('toast.fell'));
        else if (e.kind === 'sank') showToast(t('toast.sank', { n: e.elev }));
        else if (e.kind === 'skipped' && !mine)
          showToast(t('toast.skipped', { name: nameOf(e.player) }));
        else if (e.kind === 'collected' && mine)
          showToast(
            t('toast.collected', {
              n: e.amount,
              resource: resourceName(e.item, e.amount).toLowerCase(),
            }),
          );
        else if (e.kind === 'removed' && !mine)
          showToast(
            t('toast.removed', {
              name: nameOf(e.player),
              machine: machineName(e.type).toLowerCase(),
            }),
          );
        else if (e.kind === 'upgrading' && !mine)
          showToast(
            t('toast.upgrading', { name: nameOf(e.player), machine: machineOn(e.tile), n: e.to }),
          );
        else if (e.kind === 'upgraded')
          showToast(t('toast.upgraded', { machine: machineOn(e.tile), n: e.level }), true);
      }
    }),
    game.events.on('refused', ({ refusal }) => {
      if (refusal.code === 'superseded') return;
      haptic('refused');
      showToast(refusalText(refusal));
    }),
    game.events.on('member', ({ change, member }) => {
      if (member.id === game.store.get().me?.id) return;
      if (change === 'added') showToast(t('toast.member-joined', { name: member.name }));
      else if (change === 'removed') showToast(t('toast.member-left', { name: member.name }));
    }),
    game.events.on('arrived', (id) => {
      if (performance.now() - welcomedAt > 3000) showToast(t('toast.joined', { name: nameOf(id) }));
    }),
    game.events.on('welcome', () => {
      welcomedAt = performance.now();
      const s = game.store.get();
      if (!s.predicting && !warnedRules) {
        warnedRules = true;
        showToast(t('toast.rules-differ'), true);
      }
    }),
    game.store.subscribe((s, prev) => {
      if (s.world?.id !== prev.world?.id) {
        // another world: its connection starts afresh, nothing dropped
        wasOnline = false;
        outageShown = false;
      }
      if (s.connection === prev.connection) return;
      if (s.connection === 'online') {
        wasOnline = true;
        outageShown = false;
      } else if (s.connection === 'outdated') showToast(t('toast.outdated'), true);
      else if (wasOnline && s.world && !outageShown) {
        // one message per outage, not one per reconnect attempt
        outageShown = true;
        showToast(t('toast.offline'));
      }
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
